// Core modules
import { Component, Input, OnChanges } from '@angular/core';

/**
 * Question Sets Component
 *
 * Manages the `questionSets` node inside clientSettings. Each set has a unique
 * title and a list of possible values. A valid set must have a non-empty title
 * and more than one value. Titles must be unique (case-insensitive).
 */
@Component({
    selector: 'app-question-sets',
    templateUrl: './question-sets.component.html',
    styleUrls: ['./question-sets.component.scss'],
    standalone: false
})
export class QuestionSetsComponent implements OnChanges {
  @Input() settings: any;
  public questionSets: Array<{ title: string; values: string[] }> = [];

  ngOnChanges(): void {
    if (this.settings && this.settings.questionSets) {
      this.questionSets = this.settings.questionSets || [];
    }
  }

  addSet(): void {
    this.questionSets.push({ title: '', values: ['', ''] });
  }

  removeSet(idx: number): void {
    this.questionSets.splice(idx, 1);
  }

  trackByIndex(index: number): number {
    return index;
  }

  addValue(set: { title: string; values: string[] }): void {
    set.values.push('');
  }

  removeValue(set: { title: string; values: string[] }, idx: number): void {
    set.values.splice(idx, 1);
  }

  /**
   * Returns true when a set's title duplicates another set's title (case-insensitive).
   */
  isDuplicateTitle(idx: number): boolean {
    const title = (this.questionSets[idx].title || '').trim().toLowerCase();
    if (!title) {
      return false;
    }
    return this.questionSets.some(
      (s, i) => i !== idx && (s.title || '').trim().toLowerCase() === title
    );
  }

  /**
   * Number of non-empty values in a set.
   */
  private nonEmptyValueCount(set: { values: string[] }): number {
    return (set.values || []).filter(v => (v || '').trim()).length;
  }

  /**
   * Validates all sets. Returns { valid, errors }. Empty sets (no title and no
   * values) are ignored - they get stripped on save.
   */
  validate(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const seenTitles = new Set<string>();

    this.questionSets.forEach((set, idx) => {
      const title = (set.title || '').trim();
      const valueCount = this.nonEmptyValueCount(set);

      // Skip completely empty sets - they are dropped on save.
      if (!title && valueCount === 0) {
        return;
      }

      if (!title) {
        errors.push(`Set #${idx + 1} is missing a title.`);
      } else {
        const key = title.toLowerCase();
        if (seenTitles.has(key)) {
          errors.push(`Set title "${title}" is duplicated. Titles must be unique.`);
        }
        seenTitles.add(key);
      }

      if (valueCount < 2) {
        errors.push(`Set "${title || `#${idx + 1}`}" must have more than one value.`);
      }
    });

    return { valid: errors.length === 0, errors };
  }

  /**
   * Returns the cleaned sets ready to be persisted: empty sets removed and
   * blank values trimmed out.
   */
  getCleanedSets(): Array<{ title: string; values: string[] }> {
    return this.questionSets
      .map(set => ({
        title: (set.title || '').trim(),
        values: (set.values || []).map(v => (v || '').trim()).filter(v => v)
      }))
      .filter(set => set.title || set.values.length);
  }
}
